/**
 * Auxiliares puros das mensagens agendadas (testados em lib.test.ts).
 * Data e hora são as do navegador do atendente — é nelas que ele pensa.
 */
import type { ScheduledType } from './api';

const doisDigitos = (n: number): string => String(n).padStart(2, '0');

/** "2026-10-11" + "14:30" → Date no fuso do navegador. null se incompleto. */
export function combinarDataHora(data: string, hora: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
  const h = /^(\d{2}):(\d{2})$/.exec(hora);
  if (!d || !h) return null;
  const quando = new Date(
    Number(d[1]),
    Number(d[2]) - 1,
    Number(d[3]),
    Number(h[1]),
    Number(h[2]),
    0,
    0,
  );
  return Number.isNaN(quando.getTime()) ? null : quando;
}

/** Date → campos do formulário ({ data: 'YYYY-MM-DD', hora: 'HH:MM' }), fuso local. */
export function paraCampos(quando: Date): { data: string; hora: string } {
  return {
    data: `${quando.getFullYear()}-${doisDigitos(quando.getMonth() + 1)}-${doisDigitos(quando.getDate())}`,
    hora: `${doisDigitos(quando.getHours())}:${doisDigitos(quando.getMinutes())}`,
  };
}

/** Sugestão inicial do formulário: a próxima hora cheia (14:12 → 15:00). */
export function proximaHoraCheia(agora: Date): Date {
  const d = new Date(agora);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

/** Mesmo limite da API: pelo menos 1 minuto à frente. */
export function problemaNoHorario(quando: Date | null, agora: Date): string | null {
  if (!quando) return 'Escolha a data e a hora.';
  if (quando.getTime() - agora.getTime() < 60_000) {
    return 'Escolha um horário a pelo menos 1 minuto de agora.';
  }
  return null;
}

/** Tipo da mensagem pelo MIME do arquivo (mesma regra do composer). */
export function tipoDoArquivo(mime: string): Exclude<ScheduledType, 'text'> {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  return 'document';
}

/** Limites de tamanho por tipo (os do composer — limites do WhatsApp). */
export const LIMITE_POR_TIPO: Record<Exclude<ScheduledType, 'text'>, number> = {
  image: 5 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

/** "Hoje às 14:30", "Amanhã às 09:00" ou "15/10/2026 às 09:00". */
export function formatarQuando(iso: string, agora: Date): string {
  const d = new Date(iso);
  const hora = `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
  const dia = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dia(d) - dia(agora)) / 86_400_000);
  if (diff === 0) return `Hoje às ${hora}`;
  if (diff === 1) return `Amanhã às ${hora}`;
  if (diff === -1) return `Ontem às ${hora}`;
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()} às ${hora}`;
}
